import React, { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import DataViewerPopup from "./DataViewerPopup";
import JSZip from "jszip";

const formatBytes = (bytes, decimals = 2) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
};

const FileUploader = ({ onDataUpload, masterData }) => {
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDataViewerOpen, setIsDataViewerOpen] = useState(false);
  const [notification, setNotification] = useState(null);

  const showNotification = (message, type = "success") => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  const identifyFileType = (content, fileName) => {
    console.log("Identifying file type from:", fileName);

    // First try to identify by filename which is more reliable
    const fileNameLower = fileName.toLowerCase();

    if (fileNameLower === "clicks_over_time_(by_countries).csv") {
      return "clicks_countries";
    } else if (fileNameLower === "clicks_over_time_(by_platforms).csv") {
      return "clicks_platforms";
    } else if (fileNameLower === "clicks_over_time_(by_sources).csv") {
      return "clicks_sources";
    } else if (fileNameLower === "clicks_over_time_(total).csv") {
      return "clicks_total";
    } else if (fileNameLower === "impressions_over_time_(by_countries).csv") {
      return "impressions_countries";
    } else if (fileNameLower === "impressions_over_time_(by_platforms).csv") {
      return "impressions_platforms";
    } else if (fileNameLower === "impressions_over_time_(by_sources).csv") {
      return "impressions_sources";
    } else if (fileNameLower === "impressions_over_time_(total).csv") {
      return "impressions_total";
    } else if (fileNameLower === "click-through_rate_(ctr).csv") {
      return "ctr";
    } else if (fileNameLower === "plays.csv") {
      return "plays";
    }

    // Fallback to content-based identification
    const headerLine = content.split("\n")[0].toLowerCase();
    console.log("Fallback to header line:", headerLine);

    if (
      headerLine.includes("click-through rate") ||
      headerLine.includes("ctr")
    ) {
      return "ctr";
    } else if (
      headerLine.includes("united states") ||
      headerLine.includes("germany") ||
      headerLine.includes("france")
    ) {
      if (headerLine.includes("clicks")) {
        return "clicks_countries";
      } else if (headerLine.includes("impressions")) {
        return "impressions_countries";
      }
      return "geographic";
    } else if (
      headerLine.includes("console") ||
      headerLine.includes("pc") ||
      headerLine.includes("mobile")
    ) {
      if (headerLine.includes("clicks")) {
        return "clicks_platforms";
      } else if (headerLine.includes("impressions")) {
        return "impressions_platforms";
      }
      return "platform";
    } else if (
      headerLine.includes("discover") ||
      headerLine.includes("search results") ||
      headerLine.includes("recommendations")
    ) {
      if (headerLine.includes("clicks")) {
        return "clicks_sources";
      } else if (headerLine.includes("impressions")) {
        return "impressions_sources";
      }
      return "source";
    } else if (headerLine.includes("clicks")) {
      return "clicks_total";
    } else if (headerLine.includes("impressions")) {
      return "impressions_total";
    } else if (headerLine.includes("plays")) {
      return "plays";
    }

    console.log("Unknown file type for header:", headerLine);
    return "unknown";
  };

  const parseCSV = (content, type) => {
    const lines = content.split("\n").filter((line) => line.trim());
    console.log(`Parsing ${type} CSV with ${lines.length} lines`);

    // Skip header row
    const data = lines
      .slice(1)
      .map((line) => {
        const matches = line.match(/\"([^\"]*)\",?/g);

        if (matches && matches.length > 1) {
          const values = matches.map((m) =>
            m.replace(/^\"/, "").replace(/\",?$/, "").replace(/,/g, "")
          );

          const date = values[0].trim();

          switch (type) {
            case "ctr":
              return { date, value: parseFloat(values[1]) || 0 };

            case "clicks_total":
            case "impressions_total":
            case "plays":
              return { date, value: parseInt(values[1], 10) || 0 };

            case "clicks_countries":
            case "impressions_countries":
            case "clicks_platforms":
            case "impressions_platforms":
            case "clicks_sources":
            case "impressions_sources":
            case "geographic":
            case "platform":
            case "source":
              // For these types, create an object with all columns
              const result = { date };
              const headerLine = content.split("\n")[0];
              const headers = headerLine
                .match(/\"([^\"]*)\",?/g)
                .map((h) => h.replace(/^\"/, "").replace(/\",?$/, ""))
                .slice(1); // Skip date column

              headers.forEach((header, index) => {
                const value = values[index + 1]; // +1 to skip date
                result[header] = isNaN(parseInt(value))
                  ? value
                  : parseInt(value.replace(/,/g, ""), 10);
              });

              console.log(
                `Parsed ${type} data for date ${date} with ${headers.length} categories`
              );
              return result;

            default:
              return null;
          }
        }
        return null;
      })
      .filter((item) => item !== null);

    console.log(`Successfully parsed ${data.length} data points for ${type}`);
    return data;
  };

  const onDrop = useCallback(
    (acceptedFiles) => {
      setIsProcessing(true);
      const newFiles = [...uploadedFiles];

      const processCSVFile = (
        content,
        fileName,
        allFileCount,
        currentIndex,
        fileSize = null
      ) => {
        const fileType = identifyFileType(content, fileName);

        if (fileType !== "unknown") {
          const processedData = parseCSV(content, fileType);

          // Update files array
          newFiles.push({
            name: fileName,
            size: fileSize ? formatBytes(fileSize) : "N/A",
            type: fileType,
            processed: true,
          });

          // Send data to parent component
          onDataUpload(fileType, processedData);
        } else {
          newFiles.push({
            name: fileName,
            size: fileSize ? formatBytes(fileSize) : "N/A",
            type: "unknown",
            processed: false,
            error: "Unknown file format",
          });
        }

        // If this is the last file, update state
        if (currentIndex === allFileCount - 1) {
          setUploadedFiles(newFiles);
          setIsProcessing(false);
        }
      };

      const processFile = (file, index, allFiles) => {
        // Check if file is a zip file
        if (file.type === "application/zip" || file.name.endsWith(".zip")) {
          const reader = new FileReader();

          reader.onload = async () => {
            try {
              const zip = new JSZip();
              const zipContent = await zip.loadAsync(reader.result);
              const csvFiles = [];

              // Collect all CSV files in the zip
              zipContent.forEach((relativePath, zipEntry) => {
                if (
                  !zipEntry.dir &&
                  (relativePath.endsWith(".csv") ||
                    relativePath.endsWith(".CSV"))
                ) {
                  csvFiles.push(zipEntry);
                }
              });

              if (csvFiles.length === 0) {
                newFiles.push({
                  name: file.name,
                  size: formatBytes(file.size),
                  type: "zip",
                  processed: false,
                  error: "No CSV files found in the ZIP archive",
                });

                if (index === allFiles.length - 1) {
                  setUploadedFiles(newFiles);
                  setIsProcessing(false);
                }
                return;
              }

              // Process each CSV file in the zip
              let successCount = 0;
              let errorCount = 0;

              const processNextFile = async (idx) => {
                if (idx >= csvFiles.length) {
                  // All files processed
                  setUploadedFiles(newFiles);
                  setIsProcessing(false);

                  // Show success notification
                  if (successCount > 0) {
                    showNotification(
                      `Successfully processed ${successCount} CSV files from ZIP archive${
                        errorCount > 0 ? ` (${errorCount} failed)` : ""
                      }`
                    );
                  } else if (errorCount > 0) {
                    showNotification(
                      `Failed to process ${errorCount} CSV files from ZIP archive`,
                      "error"
                    );
                  }

                  return;
                }

                const zipEntry = csvFiles[idx];

                try {
                  const content = await zipEntry.async("text");
                  // Don't pass the ZIP file size to extracted CSV files
                  processCSVFile(
                    content,
                    zipEntry.name,
                    csvFiles.length,
                    idx,
                    null
                  );
                  successCount++;
                } catch (err) {
                  newFiles.push({
                    name: zipEntry.name,
                    size: "N/A",
                    type: "unknown",
                    processed: false,
                    error: "Failed to extract file from ZIP",
                  });
                  errorCount++;
                }

                // Process next file
                processNextFile(idx + 1);
              };

              // Start processing files sequentially
              processNextFile(0);
            } catch (err) {
              newFiles.push({
                name: file.name,
                size: formatBytes(file.size),
                type: "zip",
                processed: false,
                error: "Failed to process ZIP file",
              });

              if (index === allFiles.length - 1) {
                setUploadedFiles(newFiles);
                setIsProcessing(false);
              }
            }
          };

          reader.onerror = () => {
            newFiles.push({
              name: file.name,
              size: formatBytes(file.size),
              processed: false,
              error: "Failed to read file",
            });

            if (index === allFiles.length - 1) {
              setUploadedFiles(newFiles);
              setIsProcessing(false);
            }
          };

          reader.readAsArrayBuffer(file);
        } else {
          // Process regular CSV file
          const reader = new FileReader();

          reader.onload = () => {
            const content = reader.result;
            processCSVFile(
              content,
              file.name,
              allFiles.length,
              index,
              file.size
            );
          };

          reader.onerror = () => {
            newFiles.push({
              name: file.name,
              size: formatBytes(file.size),
              processed: false,
              error: "Failed to read file",
            });

            if (index === allFiles.length - 1) {
              setUploadedFiles(newFiles);
              setIsProcessing(false);
            }
          };

          reader.readAsText(file);
        }
      };

      acceptedFiles.forEach((file, index) => {
        processFile(file, index, acceptedFiles);
      });
    },
    [uploadedFiles, onDataUpload]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "text/csv": [".csv"],
      "application/vnd.ms-excel": [".csv"],
      "application/zip": [".zip"],
    },
  });

  const removeFile = (index) => {
    const newFiles = [...uploadedFiles];
    const removedFile = newFiles.splice(index, 1)[0];
    setUploadedFiles(newFiles);

    // Tell parent to remove this data
    if (removedFile.processed && removedFile.type !== "unknown") {
      onDataUpload(removedFile.type, null);
    }
  };

  const fileTypeLabels = {
    ctr: "Click-Through Rate",
    clicks_total: "Total Clicks",
    impressions_total: "Total Impressions",
    clicks_countries: "Clicks by Countries",
    impressions_countries: "Impressions by Countries",
    clicks_platforms: "Clicks by Platforms",
    impressions_platforms: "Impressions by Platforms",
    clicks_sources: "Clicks by Sources",
    impressions_sources: "Impressions by Sources",
    geographic: "Geographic Data",
    platform: "Platform Data",
    source: "Traffic Source",
    plays: "Video Plays",
    unknown: "Unknown Type",
    zip: "ZIP Archive",
  };

  return (
    <div className="space-y-6">
      {notification && (
        <div
          className={`px-4 py-3 rounded relative ${
            notification.type === "error"
              ? "bg-red-900 text-red-100 border border-red-800"
              : "bg-green-900 text-green-100 border border-green-800"
          }`}
          role="alert"
        >
          <span className="block sm:inline">{notification.message}</span>
          <button
            type="button"
            className="absolute top-0 bottom-0 right-0 px-4"
            onClick={() => setNotification(null)}
          >
            <span className="sr-only">Close</span>
            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>
      )}

      <div
        {...getRootProps()}
        className={`p-4 sm:p-8 border-2 border-dashed rounded-lg text-center cursor-pointer transition-colors
          ${
            isDragActive
              ? "border-blue-500 bg-blue-50/10"
              : "border-gray-600 hover:border-gray-500"
          }
          ${isProcessing ? "opacity-50 pointer-events-none" : ""}`}
      >
        <input {...getInputProps()} />
        <div className="flex flex-col items-center justify-center">
          <svg
            className="w-12 h-12 text-gray-400 mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
            />
          </svg>

          <p className="text-gray-300 mb-2">
            {isDragActive
              ? "Drop files here..."
              : "Drag and drop CSV or ZIP files here, or click to select files"}
          </p>
          <p className="text-sm text-gray-500 mb-2">
            Upload metrics files: CSV files directly or a ZIP containing
            multiple CSV files
          </p>
          <p className="text-xs text-indigo-400">
            Supported files: Individual CSVs or ZIP archives containing CSVs
          </p>

          {isProcessing && (
            <div className="mt-4">
              <div className="animate-pulse text-blue-400 flex items-center">
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-blue-500"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Processing files...
              </div>
            </div>
          )}
        </div>
      </div>

      {uploadedFiles.length > 0 && (
        <div className="overflow-hidden rounded-lg bg-gray-800 shadow">
          <div className="px-4 py-5 sm:p-6">
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <h3 className="text-lg font-medium text-gray-200">
                Uploaded Files
              </h3>

              {masterData && (
                <button
                  onClick={() => setIsDataViewerOpen(true)}
                  className="w-full sm:w-auto flex items-center justify-center space-x-1 px-3 py-2 bg-indigo-600 hover:bg-indigo-700 transition text-white text-sm rounded-md"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                    />
                  </svg>
                  <span>View Data Structure</span>
                </button>
              )}
            </div>

            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] divide-y divide-gray-700">
                <thead>
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      File Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Size
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {uploadedFiles.map((file, index) => (
                    <tr key={index}>
                      <td className="px-4 sm:px-6 py-4 text-sm text-gray-300 font-mono truncate max-w-xs">
                        {file.name}
                        {file.type === "zip" && !file.error && (
                          <span className="ml-2 text-xs text-green-400">
                            (ZIP Archive)
                          </span>
                        )}
                      </td>
                      <td className="px-4 sm:px-6 py-4 text-sm text-gray-300">
                        {file.size}
                      </td>
                      <td className="px-4 sm:px-6 py-4 text-sm">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium
                            ${
                              file.type === "unknown"
                                ? "bg-red-900 text-red-200"
                                : file.type === "zip"
                                ? "bg-purple-900 text-purple-200"
                                : "bg-blue-900 text-blue-200"
                            }`}
                        >
                          {fileTypeLabels[file.type] || "Unknown"}
                        </span>
                      </td>
                      <td className="px-4 sm:px-6 py-4 text-sm">
                        {file.error ? (
                          <span className="text-red-400">{file.error}</span>
                        ) : (
                          <span className="text-green-400">Processed</span>
                        )}
                      </td>
                      <td className="px-4 sm:px-6 py-4 text-sm text-right">
                        <button
                          onClick={() => removeFile(index)}
                          className="text-red-400 hover:text-red-300 transition-colors"
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Data Viewer Popup */}
      <DataViewerPopup
        isOpen={isDataViewerOpen}
        onClose={() => setIsDataViewerOpen(false)}
        data={masterData}
      />
    </div>
  );
};

export default FileUploader;
